import { CategoryTypes } from "../../../interfaces/enums";
import { CategoryWithId } from "../../../interfaces/ICategories";
import { ParticipantWithId } from "../../../interfaces/IParticipants";
import { useAgeValidation } from "../../../hooks/useAgeValidation";
import { useMemo } from "react";
/**
 * ============================================================================
 * USE PRESENTATION VALIDATION - HOOK DO WALIDACJI FORMULARZA PREZENTACJI
 * ============================================================================
 *
 * CEL HOOKA:
 * - Waliduje dane formularza prezentacji przed submitowaniem
 * - Sprawdza liczbę zawodników zgodnie z wymaganiami kategorii
 * - Waliduje pola tekstowe (nazwa, tytuł) na podstawie typu kategorii
 * - Generuje komunikaty błędów dla użytkownika
 * - Zapewnia spójne reguły walidacji dla wszystkich typów kategorii
 *
 * ARCHITEKTURA:
 * - Custom Hook Pattern: Enkapsuluje logikę walidacji w reusable hook
 * - Validation Engine: Implementuje reguły walidacji dla różnych typów kategorii
 * - Performance Optimization: Używa useMemo dla optymalizacji obliczeń
 *
 * REGUŁY WALIDACJI:
 * 1. Liczba zawodników: SOLO (min 1), DUO/TRIO/OTHER (min/max z kategorii)
 * 2. Nazwa prezentacji: SOLO/DUO/TRIO (automatyczna), OTHER (wymagana)
 * 3. Tytuł choreografii: Wymagany tylko jeśli category.isTitleRequired = true
 * 4. Walidacja wieku: (przyszła funkcjonalność - obecnie placeholder)
 *
 * PRZEPŁYW DANYCH:
 * 1. Pobiera dane do walidacji (zawodnicy, kategoria, formData)
 * 2. Sprawdza każdą regułę walidacji osobno
 * 3. Łączy wyniki w ogólny wynik walidacji
 * 4. Generuje komunikaty błędów dla nieudanych walidacji
 */


interface UsePresentationValidationProps {
  participants: ParticipantWithId[];
  category: CategoryWithId | null;
  formData: { name: string; title: string };
}

export const usePresentationValidation = ({
  participants,
  category,
  formData,
}: UsePresentationValidationProps) => {
  // ============================================================================
  // STAŁE WALIDACJI
  // ============================================================================

  const MAX_FIELD_LENGTH = 50;

  // ============================================================================
  // INICJALIZACJA WALIDACJI WIEKU
  // ============================================================================

  // Walidacja wieku zawodników - sprawdza limit odstępstwa wiekowego
  const ageValidation = useAgeValidation(participants, category);

  // ============================================================================
  // WALIDACJA LICZBY ZAWODNIKÓW
  // ============================================================================

  // Waliduje czy liczba wybranych zawodników jest zgodna z wymaganiami kategorii
  // Wejście: Brak (używa category i participants)
  // Wyjście: boolean (true jeśli liczba jest poprawna)
  // Reguły:
  // - SOLO: minimum 0 zawodników (pozwala na pustą listę - usunięcie wszystkich prezentacji)
  // - DUO/TRIO/OTHER: między minPresentationSize a maxPresentationSize
  const validateParticipantsCount = useMemo(() => {
    if (!category) return true;

    const count = participants.length;

    if (category.type === CategoryTypes.SOLO) {
      return count >= 0; // Pozwalamy na pustą listę dla SOLO
    }

    return count >= category.minPresentationSize && count <= category.maxPresentationSize;
  }, [category, participants.length]);

  // ============================================================================
  // WALIDACJA PÓL FORMULARZA
  // ============================================================================

  // Waliduje pola tekstowe formularza (nazwa, tytuł) na podstawie typu kategorii
  // Wejście: Brak (używa category i formData)
  // Wyjście: boolean (true jeśli pola są poprawne)
  // Reguły:
  // - SOLO/DUO/TRIO: nazwa jest generowana automatycznie (nie wymagana)
  // - OTHER: nazwa jest wymagana (użytkownik musi wprowadzić)
  // - Tytuł: wymagany tylko jeśli category.isTitleRequired = true
  // - Maksymalna długość: 50 znaków dla nazwy i tytułu
  const validateFormFields = useMemo(() => {
    if (!category) return true;

    // REGUŁA 1: Sprawdź czy nazwa jest wymagana (tylko dla kategorii OTHER)
    if (
      category.type !== CategoryTypes.SOLO &&
      category.type !== CategoryTypes.DUO &&
      category.type !== CategoryTypes.TRIO &&
      !(formData.name || "").trim()
    ) {
      return false;
    }

    // REGUŁA 2: Sprawdź długość nazwy (max 50 znaków)
    if (formData.name && formData.name.trim().length > MAX_FIELD_LENGTH) {
      return false;
    }

    // REGUŁA 3: Sprawdź czy tytuł choreografii jest wymagany
    if (category.isTitleRequired && !(formData.title || "").trim()) {
      return false;
    }

    // REGUŁA 4: Sprawdź długość tytułu (max 50 znaków)
    if (formData.title && formData.title.trim().length > MAX_FIELD_LENGTH) {
      return false;
    }

    return true;
  }, [category, formData]);

  // ============================================================================
  // GŁÓWNY WYNIK WALIDACJI
  // ============================================================================

  // Łączy wszystkie reguły walidacji w jeden wynik
  // Wejście: Brak (używa wyników poszczególnych walidacji)
  // Wyjście: boolean (true jeśli wszystkie walidacje przeszły)
  // Wszystkie walidacje muszą przejść: liczba zawodników + wiek + pola formularza
  const isValid = useMemo(() => {
    const result = validateParticipantsCount && ageValidation.isValid && validateFormFields;
    return result;
  }, [
    validateParticipantsCount,
    ageValidation.isValid,
    validateFormFields,
    participants.length,
    category?.type,
    formData,
  ]);

  // ============================================================================
  // GENEROWANIE KOMUNIKATÓW BŁĘDÓW
  // ============================================================================

  // Generuje komunikaty błędów dla użytkownika na podstawie nieudanych walidacji
  // Wejście: Brak (używa wyników walidacji)
  // Wyjście: string[] (lista komunikatów błędów)
  // Używane do wyświetlania alertów i tooltipów w UI
  const getValidationMessages = useMemo(() => {
    const messages: string[] = [];

    // Błąd: Nieprawidłowa liczba zawodników
    if (!validateParticipantsCount) {
      if (category?.type === CategoryTypes.SOLO) {
        // Dla SOLO nie pokazujemy błędu - pusta lista jest dozwolona
        // messages.push("Wybierz co najmniej jednego zawodnika");
      } else {
        messages.push(
          `Liczba zawodników musi być między ${category?.minPresentationSize} a ${category?.maxPresentationSize}`,
        );
      }
    }

    // Błąd: Przekroczono limit odstępstwa wiekowego (przyszła funkcjonalność)
    if (!ageValidation.isValid) {
      messages.push("Przekroczono limit odstępstwa wiekowego");
    }

    // Błąd: Nieprawidłowe pola formularza
    if (!validateFormFields) {
      if (
        category?.type !== CategoryTypes.SOLO &&
        category?.type !== CategoryTypes.DUO &&
        category?.type !== CategoryTypes.TRIO &&
        !(formData.name || "").trim()
      ) {
        messages.push("Brak nazwy prezentacji");
      }
      if (formData.name && formData.name.trim().length > MAX_FIELD_LENGTH) {
        messages.push(`Nazwa prezentacji nie może mieć więcej niż ${MAX_FIELD_LENGTH} znaków`);
      }
      if (category?.isTitleRequired && !(formData.title || "").trim()) {
        messages.push("Tytuł choreografii jest wymagany");
      }
      if (formData.title && formData.title.trim().length > MAX_FIELD_LENGTH) {
        messages.push(`Tytuł choreografii nie może mieć więcej niż ${MAX_FIELD_LENGTH} znaków`);
      }
    }

    return messages;
  }, [validateParticipantsCount, ageValidation.isValid, validateFormFields, category, formData]);

  // ============================================================================
  // EKSPORT INTERFEJSU HOOKA
  // ============================================================================

  // Zwraca wszystkie dane walidacji potrzebne do kontrolowania UI
  // Używane przez komponenty formularzy do wyświetlania błędów i kontrolowania przycisków
  return {
    isValid, // Główny wynik walidacji (wszystkie reguły)
    validateParticipantsCount, // Wynik walidacji liczby zawodników
    validateFormFields, // Wynik walidacji pól formularza
    ageValidation, // Wynik walidacji wieku (obecnie placeholder)
    validationMessages: getValidationMessages, // Komunikaty błędów dla użytkownika
  };
};
